import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {

  private readonly STORAGE_KEY = 'user_theme_color';
  private readonly DEFAULT_PRIMARY = '#3f51b5';

  public primaryColor$: BehaviorSubject<string> = new BehaviorSubject(this.DEFAULT_PRIMARY);

  constructor() {
    this.initializeTheme();
  }

  private initializeTheme(): void {
    const savedColor = localStorage.getItem(this.STORAGE_KEY);
    if (savedColor && this.isValidHexColor(savedColor)) {
      this.applyTheme(savedColor);
    }
  }

  public applyTheme(hexColor: string): void {
    if (!this.isValidHexColor(hexColor)) {
      console.warn('Invalid hex color:', hexColor);
      return;
    }

    document.documentElement.style.setProperty('--custom-primary-color', hexColor);
    document.documentElement.style.setProperty('--custom-primary-lighter', this.lighten(hexColor, 40));
    document.documentElement.style.setProperty('--custom-primary-darker', this.darken(hexColor, 20));
    document.documentElement.style.setProperty('--custom-primary-contrast', this.getContrastColor(hexColor));

    document.body.classList.add('custom-theme-active');

    localStorage.setItem(this.STORAGE_KEY, hexColor);

    this.primaryColor$.next(hexColor);
  }

  public resetTheme(): void {
    document.body.classList.remove('custom-theme-active');
    document.documentElement.style.removeProperty('--custom-primary-color');
    document.documentElement.style.removeProperty('--custom-primary-lighter');
    document.documentElement.style.removeProperty('--custom-primary-darker');
    document.documentElement.style.removeProperty('--custom-primary-contrast');
    localStorage.removeItem(this.STORAGE_KEY);
    this.primaryColor$.next(this.DEFAULT_PRIMARY);
  }

  public syncWithUser(themeColor: string | null | undefined): void {
    if (themeColor && this.isValidHexColor(themeColor)) {
      this.applyTheme(themeColor);
    } else {
      this.resetTheme();
    }
  }

  public isValidHexColor(color: string): boolean {
    return /^#[0-9A-Fa-f]{6}$/.test(color);
  }

  public getDefaultColor(): string {
    return this.DEFAULT_PRIMARY;
  }

  private lighten(hex: string, percent: number): string {
    const num = parseInt(hex.slice(1), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.min(255, (num >> 16) + amt);
    const G = Math.min(255, ((num >> 8) & 0x00FF) + amt);
    const B = Math.min(255, (num & 0x0000FF) + amt);
    return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
  }

  private darken(hex: string, percent: number): string {
    const num = parseInt(hex.slice(1), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.max(0, (num >> 16) - amt);
    const G = Math.max(0, ((num >> 8) & 0x00FF) - amt);
    const B = Math.max(0, (num & 0x0000FF) - amt);
    return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
  }

  private getContrastColor(hex: string): string {
    const num = parseInt(hex.slice(1), 16);
    const r = num >> 16;
    const g = (num >> 8) & 0x00FF;
    const b = num & 0x0000FF;
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#ffffff';
  }
}
